import React, { useState, useEffect } from "react";
// Icons are unchanged
import { FcGoogle } from "react-icons/fc";
import { FaGithub } from "react-icons/fa";
import { FiArrowLeft } from "react-icons/fi";
import { FaUser, FaUserFriends } from "react-icons/fa";
import { FaChalkboardUser } from "react-icons/fa6";
// Import Framer Motion
import { motion, AnimatePresence } from "framer-motion";
// Add supabase import at the top
import { supabase } from "../../lib/supabase";
import { useNavigate } from "react-router-dom"; // Import useNavigate
import InstitutionSelector from "../../components/InstitutionSelector";

// --- Card Component (Updated) ---
const SelectionCard = ({
  icon: Icon,
  title,
  description,
  onClick,
  isSelected,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`
            border-2 rounded-lg p-6 w-full text-left transition-all duration-200 ease-in-out
            flex items-center space-x-4
      ${
        isSelected
          ? "border-[#5379F6] bg-[#5379F6]/10 shadow-lg"
          : "border-gray-300 bg-white hover:border-[#5379F6] hover:shadow-md"
      }
      focus:outline-none focus:ring-2 focus:ring-[#5379F6] focus:ring-offset-2
    `}
  >
    <div
      className={`p-3 rounded-full ${
        isSelected ? "bg-[#5379F6] text-white" : "bg-gray-100 text-[#5379F6]"
      }`}
    >
      <Icon className="h-6 w-6" />
    </div>
    <div>
      <h3 className="text-lg font-semibold text-black">{title}</h3>
      <p className="text-base text-[#5C5C5C]">{description}</p>
    </div>
  </button>
);

// Update SubTypeButton component
const SubTypeButton = ({ title, description, onClick, isSelected }) => (
  <div className="w-full px-1">
    <button
      type="button"
      onClick={onClick}
      className={`
        border rounded-md p-3 sm:p-4 w-full text-left transition-all duration-150 ease-in-out
        min-h-[130px] sm:min-h-[145px] flex flex-col justify-center
        ${
          isSelected
            ? "border-[#5379F6] bg-[#5379F6]/10 shadow-lg"
            : "border-gray-300 bg-white hover:border-[#5379F6] hover:shadow-md"
        }
        focus:outline-none focus:ring-1 focus:ring-[#5379F6]
      `}
    >
      <h4 className="font-medium text-black text-base sm:text-xl">{title}</h4>
      <p className="text-sm sm:text-base text-[#5C5C5C] mt-1">{description}</p>
    </button>
  </div>
);

// Add these validation functions at the top of both files
const validateName = (name) => {
  return /^[a-zA-Z\s-']+$/.test(name); // Only letters, spaces, hyphens, and apostrophes
};

const validateEmail = (email) => {
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(edu|org|com)$/.test(email);
};

// Add a new useEffect to watch for subType changes
const JoinUsForm = ({ onSuccess }) => {
  // Accept onSuccess as a prop
  // --- State and handlers are mostly unchanged ---
  const [authView, setAuthView] = useState("signup");
  const [step, setStep] = useState(1);
  const [userType, setUserType] = useState(null);
  const [subType, setSubType] = useState(null);
  const [resetLinkSent, setResetLinkSent] = useState(false);
  const [direction, setDirection] = useState(1);
  const [receiveUpdates, setReceiveUpdates] = useState(false);
  const [clubName, setClubName] = useState("");
  const [institution, setInstitution] = useState("");
  const [leaderName, setLeaderName] = useState("");
  const [schoolEmail, setSchoolEmail] = useState("");
  const [clubType, setClubType] = useState("");
  const [activityLevel, setActivityLevel] = useState("");
  const [memberCount, setMemberCount] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [personalInstitution, setPersonalInstitution] = useState("");
  const [role, setRole] = useState("");
  const [department, setDepartment] = useState("");
  const [position, setPosition] = useState(""); // New state for position
  const [error, setError] = useState(""); // Error state
  const [success, setSuccess] = useState(""); // Success state
  const [loading, setLoading] = useState(false); // Loading state
  const navigate = useNavigate(); // Add this at the top with other imports

  const validateInstitutionType = async (
    institutionName,
    expectedType,
    supabase
  ) => {
    if (!institutionName) return false;

    const { data, error } = await supabase
      .from("institutions")
      .select("type")
      .eq("name", institutionName)
      .single();

    if (error || !data) return false;
    return data.type === expectedType;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Add institution type validation before submission
      if (institution) {
        const expectedType =
          subType === "high-school" ? "highschool" : "college";
        const isValid = await validateInstitutionType(
          institution,
          expectedType,
          supabase
        );

        if (!isValid) {
          setError(
            `Please select a ${
              expectedType === "highschool"
                ? "high school"
                : "college/university"
            } institution`
          );
          setLoading(false);
          return;
        }
      }

      let submissionData;
      let submissionType = userType; // 'single', 'organization', or 'faculty'

      switch (userType) {
        case "single": {
          // Validate single user required fields
          if (
            !name ||
            !email ||
            !subType ||
            !institution || 
            !position
          ) {
            setError("Please fill in all required fields for your account.");
            setLoading(false);
            return;
          }

          // Validate name
          if (!validateName(name)) {
            setError("Name should only contain letters");
            setLoading(false);
            return;
          }

          // Validate email
          if (!validateEmail(email)) {
            setError("Please use a valid .edu or .org email address");
            setLoading(false);
            return;
          }

          submissionData = {
            name: name,
            school_email: email,
            school_type: subType === "high-school" ? "highschool" : "college",
            institution: institution,
            position: position,
            receive_updates: receiveUpdates,
          };
          break;
        }

        case "organization": {
          // Validate required fields for organization
          if (
            !clubName ||
            !institution ||
            !leaderName ||
            !schoolEmail ||
            !clubType ||
            !activityLevel ||
            !memberCount ||
            !subType
          ) {
            setError(
              "Please fill in all required fields for your organization."
            );
            setLoading(false);
            return;
          }

          // Validate leader name
          if (!validateName(leaderName)) {
            setError("Leader name should only contain letters");
            setLoading(false);
            return;
          }

          // Validate email
          if (!validateEmail(schoolEmail)) {
            setError("Please use a valid email address. For now, a personal email (Gmail) is recommended.");
            setLoading(false);
            return;
          }

          // Add institution type validation
          if (!institution) {
            setError("Please select an institution");
            setLoading(false);
            return;
          }

          // Get the selected institution type from the InstitutionSelector
          const selectedInstitution = await supabase
            .from("institutions")
            .select("type")
            .eq("name", institution)
            .single();

          if (selectedInstitution.error || !selectedInstitution.data) {
            setError("Invalid institution selected");
            setLoading(false);
            return;
          }

          // Check if institution type matches the selected subType
          const institutionType = selectedInstitution.data.type;
          const expectedType =
            subType === "high-school" ? "highschool" : "college";

          if (institutionType !== expectedType) {
            setError(
              `This institution is not a ${
                expectedType === "highschool"
                  ? "high school"
                  : "college/university"
              }`
            );
            setLoading(false);
            return;
          }

          const schoolTypeValue =
            subType === "higher-education" ? "college" : "highschool";

          submissionData = {
            club_name: clubName,
            institution: institution,
            leader_name: leaderName,
            school_email: schoolEmail,
            school_type: schoolTypeValue,
            club_type: clubType,
            activity_level: activityLevel,
            member_count: memberCount,
            receive_updates: receiveUpdates,
          };
          break;
        }

        case "faculty": {
          // Validate faculty required fields
          if (
            !name ||
            !email ||
            !subType ||
            !institution || // Changed from personalInstitution
            !position
          ) {
            setError("Please fill in all required fields for your account.");
            setLoading(false);
            return;
          }

          // Validate name
          if (!validateName(name)) {
            setError("Name should only contain letters");
            setLoading(false);
            return;
          }

          // Validate email
          if (!validateEmail(email)) {
            setError("Please use a valid .edu or .org email address");
            setLoading(false);
            return;
          }

          // Add institution type validation
          if (!institution) {
            setError("Please select an institution");
            setLoading(false);
            return;
          }

          // Get the selected institution type from the InstitutionSelector
          const selectedInstitution = await supabase
            .from("institutions")
            .select("type")
            .eq("name", institution)
            .single();

          if (selectedInstitution.error || !selectedInstitution.data) {
            setError("Invalid institution selected");
            setLoading(false);
            return;
          }

          // Check if institution type matches the selected subType
          const institutionType = selectedInstitution.data.type;
          const expectedType =
            subType === "high-school" ? "highschool" : "college";

          if (institutionType !== expectedType) {
            setError(
              `This institution is not a ${
                expectedType === "highschool" ? "high school" : "college"
              }`
            );
            setLoading(false);
            return;
          }

          submissionData = {
            name: name,
            school_email: email, // Use 'school_email' for consistency in the function
            institution: institution,
            school_type: subType === "high-school" ? "highschool" : "college",
            position: position,
            receive_updates: receiveUpdates,
          };
          break;
        }
      }

      // Call the verification edge function instead of direct insert
      const { error: functionError } = await supabase.functions.invoke(
        "send-waitlist-verification",
        {
          body: { submissionData, submissionType }, // Pass both data and type
        }
      );

      if (functionError) {
        // Check for specific error messages from the function
        if (functionError.message.includes("already been submitted")) {
          throw new Error(
            "This email address is already registered. Please use a different email."
          );
        }
        throw functionError;
      }

      // Call the parent's success handler instead of setting local state
      onSuccess();
    } catch (error) {
      console.error("Error:", error);
      setError(
        error.message || "An unexpected error occurred. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Add a reset form function
  const resetForm = () => {
    setUserType(null);
    setSubType(null);
    setStep(1);
    setReceiveUpdates(false);

    // Reset all form fields
    setClubName("");
    setInstitution("");
    setLeaderName("");
    setSchoolEmail("");
    setClubType("");
    setActivityLevel("");
    setMemberCount("");
    setName("");
    setEmail("");
    setPersonalInstitution("");
    setRole("");
    setDepartment("");
    setPosition(""); // Reset position state
  };

  const selectUserTypeAndProceed = (type) => {
    setUserType(type);
    setSubType(null);
    setDirection(1);
    setStep(2);
  };

  const goBack = () => {
    setDirection(-1);
    setStep(1);
    setError(""); // Clear error message
    setSuccess(""); // Clear success message
    // Reset form fields if needed
    setInstitution("");
    setPosition("");
  };

  const switchTo = (view) => {
    setAuthView(view);
    setStep(1);
    setUserType(null);
    setSubType(null);
    setResetLinkSent(false);
  };

  // --- NEW: Animation variants ---
  const variants = {
    initial: (direction) => ({
      x: direction > 0 ? "100%" : "-100%",
      opacity: 0,
    }),
    animate: {
      x: 0,
      opacity: 1,
      transition: {
        x: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.2 },
      },
    },
    exit: (direction) => ({
      x: direction < 0 ? "100%" : "-100%",
      opacity: 0,
      transition: {
        x: { type: "spring", stiffness: 300, damping: 30 },
        opacity: { duration: 0.2 },
      },
    }),
  };

  return (
    <div className="bg-white p-8 md:p-10 rounded-lg shadow-[0_0_20px_rgba(0,0,0,0.1)] border-2 border-gray-200 w-full max-w-3xl font-inter">
      <div className="text-center mb-4">
        <h2 className="text-4xl font-bold text-black">Join Us!</h2>
        <p className="text-[#5C5C5C] font-medium mt-2">
          {step === 1
            ? "First, tell us who you are."
            : "Great! Now, let's get specific."}
        </p>
      </div>

      {/*
                This form now properly contains the animated content, allowing the overall
                component to resize and enable scrolling when needed.
            */}
      <form onSubmit={handleSubmit}>
        {/*
                    The motion.div with the 'layout' prop is the key. It will
                    animate its height as the content inside changes.
                    The 'overflow-hidden' prevents content from one step from
                    briefly appearing next to the other during the animation.
                */}
        <motion.div layout className="relative overflow-hidden">
          <AnimatePresence initial={false} custom={direction} mode="wait">
            {step === 1 && (
              <motion.div
                key="step1"
                custom={direction}
                variants={variants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="space-y-6"
              >
                <div>
                  <label className="block text-lg font-medium text-[#4A4A4A] mb-3">
                    Who is signing up for the Early Access Waitlist?
                  </label>
                  <div className="space-y-4">
                    <SelectionCard
                      icon={FaUser}
                      title="Just Me"
                      description="For your personal student account."
                      onClick={() => selectUserTypeAndProceed("single")}
                      isSelected={userType === "single"}
                    />
                    <SelectionCard
                      icon={FaUserFriends}
                      title="My Club/Organization"
                      description="For your student club/Organzation."
                      onClick={() => selectUserTypeAndProceed("organization")}
                      isSelected={userType === "organization"}
                    />
                    <SelectionCard
                      icon={FaChalkboardUser}
                      title="Faculty/Staff"
                      description="For faculty or staff members."
                      onClick={() => selectUserTypeAndProceed("faculty")}
                      isSelected={userType === "faculty"}
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                custom={direction}
                variants={variants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="space-y-4"
              >
                <button
                  type="button"
                  onClick={goBack}
                  className="flex items-center text-sm font-medium text-[#5379F6] hover:text-[#4A6ACB] focus:outline-none"
                >
                  <FiArrowLeft className="h-4 w-4 mr-1" />
                  Back to Account Type
                </button>

                {userType === "single" && (
                  <div>
                    <label className="block text-lg font-medium text-[#4A4A4A] mb-3">
                      Are you a high school or college/university student?
                    </label>
                    <div className="flex space-x-3">
                      <SubTypeButton
                        title="High School"
                        description="Currently enrolled in grades 9-12."
                        onClick={async () => {
                          const currentType = subType;
                          setSubType("high-school");

                          // If institution is selected and type is changing, verify institution type
                          if (institution && currentType !== "high-school") {
                            const isValid = await validateInstitutionType(
                              institution,
                              "highschool",
                              supabase
                            );
                            if (!isValid) {
                              setInstitution(""); // Clear invalid institution
                              setError(
                                "Please select a high school institution"
                              );
                            }
                          }
                        }}
                        isSelected={subType === "high-school"}
                      />
                      <SubTypeButton
                        title="College/University"
                        description="Currently pursuing a degree."
                        onClick={async () => {
                          const currentType = subType;
                          setSubType("higher-education");

                          // If institution is selected and type is changing, verify institution type
                          if (
                            institution &&
                            currentType !== "higher-education"
                          ) {
                            const isValid = await validateInstitutionType(
                              institution,
                              "college",
                              supabase
                            );
                            if (!isValid) {
                              setInstitution(""); // Clear invalid institution
                              setError(
                                "Please select a college/university institution"
                              );
                            }
                          }
                        }}
                        isSelected={subType === "higher-education"}
                      />
                    </div>
                  </div>
                )}
                {userType === "organization" && (
                  <div>
                    <label className="block text-lg font-medium text-[#4A4A4A] mb-3">
                      What kind of organization is this?
                    </label>
                    <div className="flex space-x-3">
                      <SubTypeButton
                        title="High School"
                        description="Secondary school education"
                        onClick={() => {
                          setSubType("high-school");
                          setInstitution(""); // Reset institution when type changes
                        }}
                        isSelected={subType === "high-school"}
                      />
                      <SubTypeButton
                        title="College/University"
                        description="Post-secondary school education"
                        onClick={() => {
                          setSubType("higher-education");
                          setInstitution(""); // Reset institution when type changes
                        }}
                        isSelected={subType === "higher-education"}
                      />
                    </div>
                  </div>
                )}
                {userType === "faculty" && (
                  <div>
                    <label className="block text-lg font-medium text-[#4A4A4A] mb-3">
                      Are you a member of the faculty or staff at a high school
                      or a college/university?
                    </label>
                    <div className="flex space-x-3">
                      <SubTypeButton
                        title="High School"
                        description="Faculty/Staff in High school"
                        onClick={() => {
                          setSubType("high-school");
                          setInstitution(""); // Reset institution when type changes
                        }}
                        isSelected={subType === "high-school"}
                      />
                      <SubTypeButton
                        title="College/University"
                        description="Faculty/Staff in College or University"
                        onClick={() => {
                          setSubType("higher-education");
                          setInstitution(""); // Reset institution when type changes
                        }}
                        isSelected={subType === "higher-education"}
                      />
                    </div>
                  </div>
                )}

                {/* This section now appears smoothly and allows scrolling */}
                <AnimatePresence>
                  {subType && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="space-y-4 pt-4 border-t border-gray-200 mt-4 overflow-hidden"
                    >
                      {/* Organization Form Fields */}
                      {userType === "organization" && (
                        <div className="space-y-4">
                          <div>
                            <label
                              htmlFor="clubName"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Club Name
                            </label>
                            <input
                              id="clubName"
                              name="clubName"
                              type="text"
                              required
                              className="mt-1 w-full px-3 py-2 text-lg border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                              onChange={(e) => setClubName(e.target.value)}
                            />
                          </div>

                          <div>
                            <label
                              htmlFor="institution"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Institution
                            </label>
                            <InstitutionSelector
                              schoolType={
                                subType === "high-school"
                                  ? "highschool"
                                  : "college"
                              }
                              required
                              onSelect={(institution) => {
                                setInstitution(institution.name);
                              }}
                            />
                          </div>

                          <div>
                            <label
                              htmlFor="leaderName"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Club Leader Full Name
                            </label>
                            <input
                              id="leaderName"
                              name="leaderName"
                              type="text"
                              required
                              className="mt-1 w-full px-3 py-2 text-lg border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                              onChange={(e) => setLeaderName(e.target.value)}
                            />
                          </div>

                          <div>
                            <label
                              htmlFor="schoolEmail"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Email
                            </label>
                            <p className="text-sm text-[#4A4A4A] mt-1 mb-2">
                              Note: Some .edu and .org emails aren’t working for now. Please use a personal email (like Gmail) to get verification link.
                            </p>
                            <input
                              id="schoolEmail"
                              name="schoolEmail"
                              type="email"
                              required
                              className="mt-1 w-full px-3 py-2 text-lg border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                              onChange={(e) => setSchoolEmail(e.target.value)}
                            />
                          </div>

                          <div>
                            <label
                              htmlFor="clubType"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Club Type
                            </label>
                            <select
                              id="clubType"
                              name="clubType"
                              required
                              className="mt-1 w-full px-3 py-2 text-lg border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                              onChange={(e) => setClubType(e.target.value)}
                            >
                              <option value="">Select club type</option>
                              <option value="academic">Academic</option>
                              <option value="cultural">Cultural</option>
                              <option value="service">Service</option>
                              <option value="sports">Sports</option>
                              <option value="arts">Arts</option>
                              <option value="technology">Technology</option>
                              <option value="other">Other</option>
                            </select>
                          </div>

                          <div>
                            <label
                              htmlFor="activityLevel"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              How active is your club?
                            </label>
                            <select
                              id="activityLevel"
                              name="activityLevel"
                              required
                              className="mt-1 w-full px-3 py-2 text-lg border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                              onChange={(e) => setActivityLevel(e.target.value)}
                            >
                              <option value="">Select activity level</option>
                              <option value="very_active">Very Active</option>
                              <option value="active">Active</option>
                              <option value="moderate">Moderate</option>
                              <option value="occasional">Occasional</option>
                            </select>
                          </div>

                          <div>
                            <label
                              htmlFor="memberCount"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Number of Active Members
                            </label>
                            <select
                              id="memberCount"
                              name="memberCount"
                              required
                              className="mt-1 w-full px-3 py-2 text-lg border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                              onChange={(e) => setMemberCount(e.target.value)}
                            >
                              <option value="">Select member count</option>
                              <option value="1-10">1-10 members</option>
                              <option value="11-25">11-25 members</option>
                              <option value="26-50">26-50 members</option>
                              <option value="51-100">51-100 members</option>
                              <option value="100+">
                                More than 100 members
                              </option>
                            </select>
                          </div>
                        </div>
                      )}

                      {/* Single User Form Fields */}
                      {userType === "single" && (
                        <div className="space-y-4">
                          <div>
                            <label
                              htmlFor="name"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Full Name
                            </label>
                            <input
                              id="name"
                              name="name"
                              type="text"
                              required
                              className="mt-1 w-full px-3 py-2 text-lg border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                            />
                          </div>

                          <div>
                            <label
                              htmlFor="personalInstitution"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Institution
                            </label>
                            <InstitutionSelector
                              schoolType={
                                subType === "high-school"
                                  ? "highschool"
                                  : "college"
                              }
                              required
                              onSelect={(institution) => {
                                setInstitution(institution.name);
                              }}
                            />
                          </div>

                          <div>
                            <label
                              htmlFor="email"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Email
                            </label>
                            <p className="text-sm text-[#4A4A4A] mt-1 mb-2">
                            Note: Some .edu and .org emails aren’t working for now. Please use a personal email (like Gmail) to get verification link.
                            </p>
                            <input
                              id="email"
                              name="email"
                              type="email"
                              required
                              className="mt-1 w-full px-3 py-2 text-lg border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                            />
                          </div>
                          <div className="py-2 text-lg">
                            <div className="flex flex-col space-y-4">
                              <label
                                htmlFor="position"
                                className="block text-lg font-medium text-[#4A4A4A]"
                              >
                                Are you a Club Leader or Student?
                              </label>
                              <div className="space-y-3">
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="checkbox"
                                    checked={position === "student"}
                                    onChange={() => setPosition("student")}
                                    className="w-4 h-4 accent-[#5379F6] border-[#5379F6] border-2 rounded focus:ring-[#5379F6]"
                                  />
                                  <label className="block text-base font-medium text-[#4A4A4A]">
                                    Student
                                  </label>
                                </div>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="checkbox"
                                    checked={position === "club_leader"}
                                    onChange={() => setPosition("club_leader")}
                                    className="w-4 h-4 accent-[#5379F6] border-[#5379F6] border-2 rounded focus:ring-[#5379F6]"
                                  />
                                  <label className="block text-base font-medium text-[#4A4A4A]">
                                    Club Leader
                                  </label>
                                </div>
                                <div className="flex items-center space-x-3">
                                  <input
                                    type="checkbox"
                                    checked={position === "student_government"}
                                    onChange={() =>
                                      setPosition("student_government")
                                    }
                                    className="w-4 h-4 accent-[#5379F6] border-[#5379F6] border-2 rounded focus:ring-[#5379F6]"
                                  />
                                  <label className="block text-base font-medium text-[#4A4A4A]">
                                    Student Government
                                  </label>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Faculty/Staff Form Fields */}
                      {userType === "faculty" && (
                        <div className="space-y-4">
                          <div>
                            <label
                              htmlFor="name"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Full Name
                            </label>
                            <input
                              id="name"
                              name="name"
                              type="text"
                              required
                              className="mt-1 w-full px-3 py-2 text-lg border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                            />
                          </div>

                          <div>
                            <label
                              htmlFor="personalInstitution"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Institution
                            </label>
                            <InstitutionSelector
                              schoolType={
                                subType === "high-school"
                                  ? "highschool"
                                  : "college"
                              }
                              required
                              onSelect={(institution) => {
                                setInstitution(institution.name);
                              }}
                            />
                          </div>

                          <div>
                            <label
                              htmlFor="email"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Email
                            </label>
                            <p className="text-sm text-[#4A4A4A] mt-1 mb-2">
                            Note: Some .edu and .org emails aren’t working for now. Please use a personal email (like Gmail) to get verification link.
                            </p>
                            <input
                              id="email"
                              name="email"
                              type="email"
                              required
                              className="mt-1 w-full px-3 py-2 text-lg border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                            />
                          </div>

                          <div>
                            <label
                              htmlFor="position"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Position/Role
                            </label>
                            <input
                              id="position"
                              name="position"
                              type="text"
                              required
                              placeholder="Enter your position"
                              className="mt-1 w-full px-3 py-2 text-lg border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                              value={position}
                              onChange={(e) => setPosition(e.target.value)}
                            />
                          </div>
                        </div>
                      )}

                      {/* Common Fields for All Users */}
                      <hr />
                      <div className="pt-2 pb-4 flex items-center space-x-3">
                        <input
                          type="checkbox"
                          id="updates"
                          name="receiveUpdates"
                          className="w-4 h-4 accent-[#5379F6] border-[#5379F6] border-2 rounded focus:ring-[#5379F6]"
                          checked={receiveUpdates}
                          onChange={(e) => setReceiveUpdates(e.target.checked)}
                        />
                        <label
                          htmlFor="updates"
                          className="block text-lg font-medium text-[#4A4A4A]"
                        >
                          I'd like to receive updates about Scholarty
                        </label>
                      </div>
                      <div className="w-full space-y-4 mt-8">
                        <div className="w-full px-1">
                          <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex justify-center py-4 px-6 border border-transparent rounded-md shadow-sm text-xl font-medium text-white bg-[#5379F6] hover:bg-[#4A6ACB] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5379F6] disabled:opacity-50"
                          >
                            {loading ? "Joining..." : "Join the Waitlist"}
                          </button>
                        </div>
                      </div>

                      {/* Error and Success Messages */}
                      <div className="mt-4">
                        {error && (
                          <div className="text-red-500 text-center">
                            {error}
                          </div>
                        )}
                        {success && (
                          <div className="text-green-500 text-center">
                            {success}
                          </div>
                        )}
                        {loading && (
                          <div className="text-blue-500 text-center">
                            Submitting...
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </form>
    </div>
  );
};

export default JoinUsForm;
